import { lazy } from 'react';
import { Box, Text, Group, Avatar, Badge, ScrollArea, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { IconBeach, IconMedicalCross, IconHome, IconDots } from '@tabler/icons-react';
import { WidgetProps, LeaveRequestData, UserData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// PeopleOnLeave Widget Component
// ============================================================================

function PeopleOnLeaveWidget({ globalData, isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const leaveRequests = globalData?.leaveRequests || [];
  const users = globalData?.users || [];

  // Filter to approved and active leave requests
  const today = new Date().toISOString().split('T')[0];
  const activeLeaves = leaveRequests.filter((leave) => {
    return leave.status === 'approved' && leave.startDate <= today && leave.endDate >= today;
  });

  // Get user info for each leave
  const leavesWithUsers = activeLeaves.map((leave) => {
    const user = users.find((u) => u.id === leave.userId);
    return { ...leave, user };
  });

  const getLeaveIcon = (type: LeaveRequestData['type']) => {
    const icons = {
      vacation: IconBeach,
      sick: IconMedicalCross,
      personal: IconHome,
      other: IconDots,
    };
    return icons[type] || IconDots;
  };

  const getLeaveColor = (type: LeaveRequestData['type']) => {
    const colors = {
      vacation: 'blue',
      sick: 'red',
      personal: 'orange',
      other: 'gray',
    };
    return colors[type] || 'gray';
  };

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

    if (start === end) {
      return startDate.toLocaleDateString('en-US', options);
    }
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  if (leavesWithUsers.length === 0) {
    return (
      <FadeIn style={{ height: '100%' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: theme.colors.gray[5],
          }}
        >
          <IconBeach size={32} stroke={1.5} />
          <Text size="sm" mt="xs" style={accessibilityStyles}>
            No one is on leave today
          </Text>
        </Box>
      </FadeIn>
    );
  }

  return (
    <FadeIn style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <ScrollArea style={{ flex: 1 }}>
        {leavesWithUsers.map((leave, index) => {
          const LeaveIcon = getLeaveIcon(leave.type);
          return (
            <Box
              key={leave.id}
              p="xs"
              mb="xs"
              style={{
                backgroundColor: index % 2 === 0
                  ? (colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0])
                  : 'transparent',
                borderRadius: theme.radius.sm,
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={leave.user?.avatar}
                    size="sm"
                    radius="xl"
                    color="blue"
                  >
                    {leave.user?.name?.charAt(0) || '?'}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
                      {leave.user?.name || 'Unknown User'}
                    </Text>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      {formatDateRange(leave.startDate, leave.endDate)}
                    </Text>
                  </Box>
                </Group>
                <Badge
                  size="sm"
                  variant="light"
                  color={getLeaveColor(leave.type)}
                  leftSection={<LeaveIcon size={12} />}
                >
                  {leave.type}
                </Badge>
              </Group>
            </Box>
          );
        })}
      </ScrollArea>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'people-on-leave',
  name: 'People on Leave',
  description: 'Team members currently on vacation, sick leave, or personal time',
  icon: IconBeach,
  category: 'people',
  component: lazy(() => Promise.resolve({ default: PeopleOnLeaveWidget })),
  sizes: {
    base: { cols: 12, rows: 4 },
    sm: { cols: 12, rows: 4 },
    md: { cols: 6, rows: 4 },
    lg: { cols: 4, rows: 4 },
    xl: { cols: 3, rows: 4 },
    tv: { cols: 3, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 3 },
        sm: { cols: 6, rows: 3 },
        md: { cols: 4, rows: 3 },
        lg: { cols: 3, rows: 3 },
        xl: { cols: 2, rows: 3 },
        tv: { cols: 2, rows: 3 },
      },
      standard: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 6, rows: 4 },
        lg: { cols: 4, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      tall: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 6, rows: 6 },
        lg: { cols: 4, rows: 6 },
        xl: { cols: 3, rows: 6 },
        tv: { cols: 3, rows: 6 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 8, rows: 4 },
        xl: { cols: 6, rows: 4 },
        tv: { cols: 6, rows: 4 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['leaveRequests', 'users'],
  },
  hasSettings: true,
});

export default PeopleOnLeaveWidget;
